import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from '../config/api';
import { API_URL } from '../config/api';
import { MdCameraAlt, MdCheckCircle, MdError, MdPerson, MdVerified } from 'react-icons/md';

function VerifyIdentityPage() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [capturing, setCapturing] = useState(false);
  const [capturedImage, setCapturedImage] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const videoRef = useRef(null);
  const streamRef = useRef(null);

  const fetchInfo = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/verification/info/${token}`);
      setInfo(res.data);
      if (res.data.status !== 'pending') {
        setError(res.data.message || 'This verification has already been processed.');
      }
    } catch (err) {
      const status = err.response?.status;
      if (status === 404) {
        setError('Invalid or expired verification link. Please generate a new QR code from your dashboard.');
      } else {
        setError(err.response?.data?.detail || 'Something went wrong. Please try again later.');
      }
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchInfo();
    return () => stopCamera();
  }, [token, fetchInfo]);

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: { ideal: 640 }, height: { ideal: 480 } }
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
      setCapturing(true);
    } catch {
      setError('Unable to access camera. Please allow camera access and try again.');
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
  };

  const capturePhoto = () => {
    if (!videoRef.current) return;
    const canvas = document.createElement('canvas');
    canvas.width = videoRef.current.videoWidth;
    canvas.height = videoRef.current.videoHeight;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(videoRef.current, 0, 0);
    const dataUrl = canvas.toDataURL('image/jpeg', 0.85);
    setCapturedImage(dataUrl);
    stopCamera();
    setCapturing(false);
  };

  const retakePhoto = () => {
    setCapturedImage(null);
    startCamera();
  };

  const submitSelfie = async () => {
    if (!capturedImage) return;
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/verification/submit/${token}`, {
        image_data: capturedImage,
        content_type: 'image/jpeg'
      });
      setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to submit selfie. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <MdCheckCircle className="text-4xl text-green-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Selfie Submitted</h1>
          <p className="text-gray-600">
            Your identity verification is under review. An admin will compare your selfie to your profile photo and you'll be notified of the result.
          </p>
          <p className="text-sm text-gray-400 mt-4">You can close this page now.</p>
        </div>
      </div>
    );
  }

  if (error && !info?.profile) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <MdError className="text-4xl text-red-600" />
          </div>
          <h1 className="text-xl font-bold text-gray-900 mb-2">Verification Unavailable</h1>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-lg max-w-md w-full overflow-hidden">
        {/* Header */}
        <div className="bg-primary p-6 text-center text-white">
          <MdVerified className="text-4xl mx-auto mb-2" />
          <h1 className="text-xl font-bold">Identity Verification</h1>
          <p className="text-sm text-white/80 mt-1">Take a selfie to verify your identity</p>
        </div>

        {/* Profile Reference */}
        {info?.profile && (
          <div className="p-4 border-b border-gray-100">
            <p className="text-xs text-gray-500 uppercase tracking-wide mb-2">Verifying identity for</p>
            <div className="flex items-center gap-3">
              <div className="w-14 h-14 rounded-lg bg-gray-200 flex items-center justify-center overflow-hidden flex-shrink-0 border border-gray-300">
                {info.profile.profile_picture_url ? (
                  <img src={info.profile.profile_picture_url} alt="Profile" className="w-full h-full object-cover" />
                ) : (
                  <MdPerson className="text-2xl text-gray-400" />
                )}
              </div>
              <div>
                <p className="font-semibold text-gray-900">{info.profile.name}</p>
                <p className="text-xs text-gray-500">Your selfie will be compared to this profile photo</p>
              </div>
            </div>
          </div>
        )}

        {/* Camera / Capture Area */}
        <div className="p-4">
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}

          {!capturing && !capturedImage && (
            <div className="text-center py-6">
              <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <MdCameraAlt className="text-4xl text-gray-400" />
              </div>
              <p className="text-sm text-gray-600 mb-4">
                Position your face clearly in the camera frame. Make sure you're in a well-lit area.
              </p>
              <button
                onClick={startCamera}
                className="w-full px-6 py-3 bg-primary text-white rounded-xl font-medium hover:bg-primary/90 transition-colors flex items-center justify-center gap-2"
                data-testid="open-camera-btn"
              >
                <MdCameraAlt className="text-xl" />
                Open Camera
              </button>
            </div>
          )}

          {capturing && (
            <div>
              <div className="rounded-xl overflow-hidden bg-black mb-4 aspect-[4/3]">
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
                  className="w-full h-full object-cover mirror"
                  style={{ transform: 'scaleX(-1)' }}
                />
              </div>
              <button
                onClick={capturePhoto}
                className="w-full px-6 py-3 bg-primary text-white rounded-xl font-medium hover:bg-primary/90 transition-colors flex items-center justify-center gap-2"
                data-testid="capture-selfie-btn"
              >
                <MdCameraAlt className="text-xl" />
                Take Photo
              </button>
            </div>
          )}

          {capturedImage && (
            <div>
              <div className="rounded-xl overflow-hidden mb-4 border border-gray-200">
                <img src={capturedImage} alt="Selfie" className="w-full" style={{ transform: 'scaleX(-1)' }} />
              </div>
              <div className="flex gap-3">
                <button
                  onClick={retakePhoto}
                  className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-xl font-medium hover:bg-gray-50 transition-colors"
                  data-testid="retake-btn"
                >
                  Retake
                </button>
                <button
                  onClick={submitSelfie}
                  disabled={submitting}
                  className="flex-1 px-4 py-3 bg-primary text-white rounded-xl font-medium hover:bg-primary/90 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="submit-selfie-btn"
                >
                  {submitting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Submitting...
                    </>
                  ) : (
                    'Submit Selfie'
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default VerifyIdentityPage;
